/**
 * ViewBox calculation helpers for SVG pan/zoom functionality.
 * Pure functions with no Angular dependencies - easily testable.
 */

export interface ViewBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

/**
 * Compute a ViewBox that tightly wraps the SVG content bounding box with padding.
 * Returns null if bbox is invalid.
 */
export function computeContentFittedViewBox(
  bbox: DOMRect,
  padding: number
): ViewBox | null {
  if (!isFinite(bbox.width) || !isFinite(bbox.height) || bbox.width <= 0 || bbox.height <= 0) {
    return null;
  }

  return {
    x: bbox.x - padding,
    y: bbox.y - padding - 5,
    width: bbox.width + (padding * 2),
    height: bbox.height + (padding * 2)
  };
}

/**
 * Compute new ViewBox after zoom operation around mouse cursor position.
 * @param currentViewBox - Current SVG viewBox
 * @param mouseClientPosition - Mouse position in client coordinates
 * @param svgRect - SVG element's bounding rect
 * @param wheelDelta - Mouse wheel delta (positive = zoom out, negative = zoom in)
 * @param zoomIntensity - Zoom speed factor (default 0.1)
 * @returns New ViewBox after zoom
 */
export function computeZoomViewBox(
  currentViewBox: ViewBox,
  mouseClientPosition: Point,
  svgRect: DOMRect,
  wheelDelta: number,
  zoomIntensity: number = 0.1
): ViewBox {
  const delta = wheelDelta > 0 ? 1 + zoomIntensity : 1 - zoomIntensity;
  
  // Get mouse position relative to SVG element
  const mouseX = mouseClientPosition.x - svgRect.left;
  const mouseY = mouseClientPosition.y - svgRect.top;
  
  // Convert to viewBox coordinates
  const svgX = currentViewBox.x + (mouseX / svgRect.width) * currentViewBox.width;
  const svgY = currentViewBox.y + (mouseY / svgRect.height) * currentViewBox.height;
  
  // Compute new viewBox dimensions
  const newWidth = currentViewBox.width * delta;
  const newHeight = currentViewBox.height * delta;
  
  return {
    x: svgX - (mouseX / svgRect.width) * newWidth,
    y: svgY - (mouseY / svgRect.height) * newHeight,
    width: newWidth,
    height: newHeight
  };
}

/**
 * Compute new ViewBox after pan operation (mouse drag).
 * @param currentViewBox - Current SVG viewBox
 * @param startPosition - Pan start position in client coordinates
 * @param currentPosition - Current mouse position in client coordinates
 * @param scale - Current zoom scale factor
 * @returns New ViewBox after pan
 */
export function computePanViewBox(
  currentViewBox: ViewBox,
  startPosition: Point,
  currentPosition: Point,
  scale: number
): ViewBox {
  const dx = (currentPosition.x - startPosition.x) / scale;
  const dy = (currentPosition.y - startPosition.y) / scale;
  
  return {
    x: currentViewBox.x - dx,
    y: currentViewBox.y - dy,
    width: currentViewBox.width,
    height: currentViewBox.height
  };
}

/**
 * Compute new ViewBox after shift+scroll pan operation.
 * @param currentViewBox - Current SVG viewBox
 * @param deltaX - Horizontal scroll delta
 * @param deltaY - Vertical scroll delta
 * @param scale - Current zoom scale factor
 * @returns New ViewBox after pan
 */
export function computeShiftPanViewBox(
  currentViewBox: ViewBox,
  deltaX: number,
  deltaY: number,
  scale: number
): ViewBox {
  const dx = deltaX / scale;
  const dy = deltaY / scale;
  
  return {
    x: currentViewBox.x + dx * 2.25,
    y: currentViewBox.y + dy * 2.25,
    width: currentViewBox.width,
    height: currentViewBox.height
  };
}

/**
 * Compute the new scale factor after zoom operation.
 * @param currentScale - Current scale
 * @param wheelDelta - Mouse wheel delta
 * @param zoomIntensity - Zoom speed factor
 * @returns New scale factor
 */
export function computeNewScale(
  currentScale: number,
  wheelDelta: number,
  zoomIntensity: number = 0.1
): number {
  const delta = wheelDelta > 0 ? 1 + zoomIntensity : 1 - zoomIntensity;
  return currentScale / delta;
}

/**
 * Format ViewBox object as SVG viewBox attribute string.
 */
export function formatViewBoxString(viewBox: ViewBox): string {
  return `${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`;
}

/**
 * Parse SVG viewBox attribute string into ViewBox object.
 * Returns null if invalid.
 */
export function parseViewBoxString(viewBoxAttr: string): ViewBox | null {
  const parts = viewBoxAttr.split(' ').map(Number);
  if (parts.length !== 4 || parts.some(n => !isFinite(n))) {
    return null;
  }
  return {
    x: parts[0],
    y: parts[1],
    width: parts[2],
    height: parts[3]
  };
}
